import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { userAPI } from "../../services/api";
import { LEAD_STAGE, leadStageToRouteMap } from "../../Utils/constants";
import useLeadStage from "../../hooks/useLeadStage";
import PageLoader from "../../components/Loader";
import Toast from "../../components/Toast";
import FlowFooter from "../../components/FlowFooter";

const curr_page_lead_stage = [LEAD_STAGE.KYC_DOC_UPLOADED];

function References() {
  const navigate = useNavigate();
  const { leadStage, isLoadingStage } = useLeadStage();
  const [references, setReferences] = useState({
    reference1: {
      name: "",
      number: "",
      relation: "",
    },
    reference2: {
      name: "",
      number: "",
      relation: "",
    },
  });

  const relations = [
    "Parents",
    "Relatives",
    "Spouse",
    "Friend",
    "Colleague",
    "Sibling",
    "Other",
  ];
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState("");
  const [toast, setToast] = useState({ show: false, message: "", type: "warning" });

  const showToast = (message, type = "error") => {
    setToast({ show: true, message, type });
  };

  const hideToast = () => {
    setToast({ show: false, message: "", type: "warning" });
  };

  // Handle lead stage navigation
  useEffect(() => {
    if (
      leadStage &&
      !isLoadingStage &&
      !curr_page_lead_stage.includes(leadStage)
    ) {
      navigate(leadStageToRouteMap[leadStage]);
    }
  }, [leadStage, isLoadingStage, navigate]);

  const validateMobile = (mobile) => {
    // Indian mobile number validation: starts with 6-9 and exactly 10 digits
    return /^[6-9]\d{9}$/.test(mobile);
  };

  const handleChange = (ref, field, value) => {
    // For mobile number field: only allow digits, max 10 digits
    if (field === "number") {
      const numericValue = value.replace(/\D/g, ''); // Remove all non-digits
      if (numericValue.length <= 10) {
        setReferences((prev) => ({
          ...prev,
          [ref]: {
            ...prev[ref],
            [field]: numericValue,
          },
        }));
        setErrors((prev) => ({ ...prev, [ref]: "" }));
      }
      return;
    }

    // For name field: only allow letters, spaces, and common name characters (no symbols)
    if (field === "name") {
      // Allow letters (including Unicode for international names), spaces, apostrophes, hyphens, and dots
      const sanitizedValue = value.replace(/[^a-zA-Z\s'.-]/g, '');
      setReferences((prev) => ({
        ...prev,
        [ref]: {
          ...prev[ref],
          [field]: sanitizedValue,
        },
      }));
      setErrors((prev) => ({ ...prev, [ref]: "" }));
      return;
    }

    // For relation field: just update the value
    if (field === "relation") {
      setReferences((prev) => ({
        ...prev,
        [ref]: {
          ...prev[ref],
          [field]: value,
        },
      }));
      setErrors((prev) => ({ ...prev, [ref]: "" }));
      return;
    }

    setReferences((prev) => ({
      ...prev,
      [ref]: {
        ...prev[ref],
        [field]: value,
      },
    }));
    setErrors((prev) => ({ ...prev, [ref]: "" }));
  };

  const validateForm = () => {
    const newErrors = {};
    const errorMessages = [];

    // Validate Reference 1
    if (!references.reference1.name.trim()) {
      newErrors.reference1 = "Please enter Reference 1 name";
      errorMessages.push("Please enter Reference 1 name");
    } else if (references.reference1.name.trim().length < 2) {
      newErrors.reference1 = "Name must be at least 2 characters";
      errorMessages.push("Reference 1 name must be at least 2 characters");
    } else if (!references.reference1.number) {
      newErrors.reference1 = "Please enter Reference 1 mobile number";
      errorMessages.push("Please enter Reference 1 mobile number");
    } else if (references.reference1.number.length !== 10) {
      newErrors.reference1 = "Mobile number must be exactly 10 digits";
      errorMessages.push("Reference 1 mobile number must be exactly 10 digits");
    } else if (!validateMobile(references.reference1.number)) {
      newErrors.reference1 = "Please enter a valid 10-digit mobile number starting with 6-9";
      errorMessages.push("Reference 1: Please enter a valid 10-digit mobile number starting with 6-9");
    } else if (!references.reference1.relation) {
      newErrors.reference1 = "Please select Reference 1 relation";
      errorMessages.push("Please select Reference 1 relation");
    }

    // Validate Reference 2
    if (!references.reference2.name.trim()) {
      newErrors.reference2 = "Please enter Reference 2 name";
      errorMessages.push("Please enter Reference 2 name");
    } else if (references.reference2.name.trim().length < 2) {
      newErrors.reference2 = "Name must be at least 2 characters";
      errorMessages.push("Reference 2 name must be at least 2 characters");
    } else if (!references.reference2.number) {
      newErrors.reference2 = "Please enter Reference 2 mobile number";
      errorMessages.push("Please enter Reference 2 mobile number");
    } else if (references.reference2.number.length !== 10) {
      newErrors.reference2 = "Mobile number must be exactly 10 digits";
      errorMessages.push("Reference 2 mobile number must be exactly 10 digits");
    } else if (!validateMobile(references.reference2.number)) {
      newErrors.reference2 = "Please enter a valid 10-digit mobile number starting with 6-9";
      errorMessages.push("Reference 2: Please enter a valid 10-digit mobile number starting with 6-9");
    } else if (!references.reference2.relation) {
      newErrors.reference2 = "Please select Reference 2 relation";
      errorMessages.push("Please select Reference 2 relation");
    }

    // Check if both numbers are same
    if (references.reference1.number && references.reference2.number &&
      references.reference1.number === references.reference2.number) {
      newErrors.reference2 = "Reference 2 mobile number cannot be same as Reference 1";
      errorMessages.push("Reference 2 mobile number cannot be same as Reference 1");
    }

    setErrors(newErrors);

    // Return validation result and error message for toast
    const isValid = Object.keys(newErrors).length === 0;
    return { isValid, errorMessage: errorMessages.length > 0 ? errorMessages[0] : "" };
  };

  const handleSubmit = async () => {
    const validation = validateForm();

    if (!validation.isValid) {
      showToast(validation.errorMessage, "error");
      return;
    }

    setIsLoading(true);
    setApiError(""); // Clear any previous API errors
    try {
      const requestData = {
        ref_no_1: references.reference1.number,
        ref_no_2: references.reference2.number,
        ref_name_1: references.reference1.name,
        ref_name_2: references.reference2.name,
        relation_1: references.reference1.relation,
        relation_2: references.reference2.relation,
      };
      await userAPI.addReference(requestData);
      navigate("/apply/e-sign");
    } catch (error) {
      // console.error("Error submitting references:", error);
      const errorMessage = error.response?.data?.message || "An error occurred while submitting references";
      setApiError(errorMessage);
      showToast(errorMessage, "error");
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoadingStage || !leadStage) {
    return <PageLoader />;
  }

  const inputClass = "w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl sm:rounded-2xl bg-white border border-[#E2E8F0] text-sm sm:text-base text-[#1E293B] placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/40 focus:border-[#FF6B35] transition-all";
  const selectClass = "w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl sm:rounded-2xl bg-white border border-[#E2E8F0] text-sm sm:text-base text-[#1E293B] focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/40 focus:border-[#FF6B35] transition-all [&>option]:bg-white [&>option]:text-[#1E293B]";

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] via-[#FFFFFF] to-[#F1F5F9] text-[#1E293B] relative flex flex-col">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="hidden sm:block absolute -top-20 -right-10 w-80 h-80 bg-[#FF6B35]/10 blur-[120px]"></div>
        <div className="hidden sm:block absolute top-32 -left-10 w-72 h-72 bg-[#1E3A8A]/10 blur-[110px]"></div>
        <div className="hidden sm:block absolute bottom-0 right-1/3 w-96 h-96 bg-[#FF6B35]/5 blur-[130px]"></div>
      </div>
      <div className="relative flex-1 flex flex-col pt-20 sm:pt-24 md:pt-28 pb-6 sm:pb-10 px-3 sm:px-4 md:px-6 lg:px-10 mt-0 overflow-y-auto">
        <div className="max-w-xl w-full mx-auto space-y-4 sm:space-y-6 py-2 sm:py-4">
          <div className="text-center mb-4 sm:mb-6">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#1E3A8A]">
              Refer & Earn <span className="text-[#FF6B35]">Gifts</span> and Vouchers!
            </h1>
            <p className="text-xs sm:text-sm md:text-base text-[#64748B] mt-2">Please enter your references below</p>
          </div>

          <div className="bg-white border border-[#E2E8F0] rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 shadow-2xl shadow-[#FF6B35]/10">
            <div className="space-y-4 sm:space-y-6">
              <div>
                <h3 className="text-xs sm:text-sm font-semibold text-[#FF6B35] mb-2 sm:mb-3">Reference 1</h3>
                <div className="space-y-2 sm:space-y-3">
                  <input type="text" value={references.reference1.name} onChange={(e) => handleChange("reference1", "name", e.target.value)} placeholder="Enter Full Name" maxLength={50} className={inputClass} />
                  <input type="tel" value={references.reference1.number} onChange={(e) => handleChange("reference1", "number", e.target.value)} placeholder="Enter 10-digit mobile number" maxLength={10} className={inputClass} />
                  <select value={references.reference1.relation} onChange={(e) => handleChange("reference1", "relation", e.target.value)} className={selectClass}>
                    <option value="">Select Relation</option>
                    {relations.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                  {errors.reference1 && <p className="text-xs text-red-500">{errors.reference1}</p>}
                </div>
              </div>

              <div>
                <h3 className="text-xs sm:text-sm font-semibold text-[#FF6B35] mb-2 sm:mb-3">Reference 2</h3>
                <div className="space-y-2 sm:space-y-3">
                  <input type="text" value={references.reference2.name} onChange={(e) => handleChange("reference2", "name", e.target.value)} placeholder="Enter Full Name" maxLength={50} className={inputClass} />
                  <input type="tel" value={references.reference2.number} onChange={(e) => handleChange("reference2", "number", e.target.value)} placeholder="Enter 10-digit mobile number" maxLength={10} className={inputClass} />
                  <select value={references.reference2.relation} onChange={(e) => handleChange("reference2", "relation", e.target.value)} className={selectClass}>
                    <option value="">Select Relation</option>
                    {relations.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                  {errors.reference2 && <p className="text-xs text-red-500">{errors.reference2}</p>}
                </div>
              </div>

              {apiError && <p className="text-xs sm:text-sm text-red-500 text-center">{apiError}</p>}

              <button
                type="button"
                disabled={isLoading}
                onClick={handleSubmit}
                className="w-full py-3 sm:py-3.5 rounded-xl sm:rounded-2xl bg-gradient-to-r from-[#FF6B35] to-[#ff8c42] text-white text-sm sm:text-base font-semibold hover:opacity-90 transition-all disabled:opacity-70 disabled:cursor-not-allowed shadow-md hover:shadow-lg flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin h-4 w-4 sm:h-5 sm:w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Processing...</span>
                  </>
                ) : (
                  <>Submit <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M6 4l8 6-8 6V4z" /></svg></>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      {toast.show && (
        <Toast message={toast.message} type={toast.type} onClose={hideToast} />
      )}
      <FlowFooter />
    </div>
  );
}

export default References;
